import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties } from 'react';
import { Fragment, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { useIsomorphicLayoutEffect, useResizeObserver } from 'usehooks-ts';

import s from './truncate-segments.scss';

// The stylesheet inverts this into `flex-shrink`, so zero or less would divide by
// zero and leave that segment unable to give up any space at all.
const weightStyle = (weight: number): CSSProperties =>
  ({ '--truncate-segments-weight': weight > 0 ? weight : 1 }) as CSSProperties;

export interface TruncateSegment {
  content: string;
  /**
   * The share of the line this segment claims when there is not room for all of
   * them, taken relative to the other segments and to its own length. Raising it
   * keeps this segment intact for longer and truncates the others sooner.
   *
   * @default 1
   */
  weight?: number;
}

export interface TruncateSegmentsProps {
  /**
   * The pieces of the line, in display order.
   */
  segments: TruncateSegment[];
  className?: string;
  /**
   * Placed between segments, and never truncated.
   *
   * @default ' '
   */
  separator?: string;
}

/**
 * Lays out several pieces of text on one line, each truncating with its own
 * ellipsis so one long piece cannot crowd the others out, under a single
 * tooltip carrying the whole line.
 */
export const TruncateSegments = ({
  segments,
  className,
  separator = ' ',
}: TruncateSegmentsProps) => {
  useStyles(s);

  const ref = useRef<HTMLSpanElement>(null);

  const { width } = useResizeObserver({ ref });

  const [isOverflowing, setIsOverflowing] = useState(false);

  const fullText = segments.map(({ content }) => content).join(separator);

  // Separators never shrink, so any clipped child is a truncated segment.
  useIsomorphicLayoutEffect(() => {
    const root = ref.current;
    if (root === null) return;

    setIsOverflowing(
      Array.from(root.children).some(
        child => child.scrollWidth > child.clientWidth,
      ),
    );
  }, [fullText, width]);

  const trigger = (
    <span ref={ref} className={classNames(s.root, className)}>
      {segments.map(({ content, weight = 1 }, index) => (
        // Segments are positional, and two can legitimately hold the same text.
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {index > 0 && <span className={s.separator}>{separator}</span>}

          <span className={s.segment} style={weightStyle(weight)}>
            {content}
          </span>
        </Fragment>
      ))}
    </span>
  );

  return (
    <Popup
      role="tooltip"
      className={s.popup}
      content={fullText}
      disabled={!isOverflowing}
      mouseEnterDelay={250}
      position="top center"
      trigger={trigger}
    />
  );
};

export default TruncateSegments;
